import { useState, useEffect } from 'react';
import { Role, User, VersionHistory } from '../services/Types';
import { getVersionHistory } from '../services/WorkflowService';

interface VersionHistoryResponse {
  versionHistory: {
    statusCode: number;
    data: VersionHistory[];
  }
}

interface Section {
  name: string;
  fields: string[];
}

const sections: Section[] = [
  { name: 'basicInfo', fields: ['eventType', 'publicOrPrivate', 'requester'] },
  { name: 'eventInfo', fields: ['title', 'summary', 'description'] },
  { name: 'speakerAndStaff', fields: ['staff'] },
  { name: 'audience', fields: ['overHundredParticipants', 'sendInvitation', 'invitationDistributionList'] },
  { name: 'datesAndTimes', fields: ['roomBooking'] },
  { name: 'featuresAndRequests', fields: ['logisticNeed', 'supportNeeded', 'additionalRequest'] }
];

export const lastReviewedMarker = "last_reviewed_marker"
export const lastRequestMarker = "end_edits_marker"

export const useVersionHistory = (eventId: number) => {
  const [versionHistory, setVersionHistory] = useState<VersionHistory[]>([]);
  const user = JSON.parse(localStorage.getItem('user') || '{}') as User

  const fetchVersionHistory = async () => {
    try {
      const { data }: { data: VersionHistoryResponse } = await getVersionHistory(eventId, 0);
      if (data.versionHistory.statusCode === 200) {
        setVersionHistory(data.versionHistory.data);
      }
    } catch (error) {
      console.error('Error getting version history:', error);
    }
  };

  useEffect(() => {
    if (eventId) {
      fetchVersionHistory();
    }
  }, [eventId]);

  const hasSectionChanged = (section: string) => {
    if (versionHistory?.length > 0) {
      const sectionFields = sections.find(s => s.name === section)?.fields;
      if (!sectionFields) return [];

      let lastMarker = lastReviewedMarker
      if (user.role === Role.REQUESTER) {
        lastMarker = lastRequestMarker
      }
      // get all the versionHistory entries since the last edit marker
      const lastMarkerIndex = versionHistory.findIndex(version => version.changes.some(change => change.field === lastMarker))
      if (lastMarkerIndex > 0) {
        const versionHistorySinceLastMarker = versionHistory.slice(0, lastMarkerIndex)

        // get all the changes for the section
        return versionHistorySinceLastMarker
          .flatMap(version => version.changes)
          .filter(change => sectionFields.includes(change.field))
          // remove duplicates
          .filter((change, index, self) => 
            index === self.findIndex(c => c.field === change.field)
          );
      }
    }
    return []
  };

  // TODO: function to determine last approver to update history
  // TODO: use globalHistoryState
  // TODO: function to get all the approvers for an event
  // TODO: function to get the last approver to update history

  return {
    versionHistory,
    hasSectionChanged,
  };
}; 